import React from "react";
import { motion } from "framer-motion";
import { CURRICULUM_FEATURES, CURRICULUM_AUDIENCES } from "../../lib/content";

const ICON_MAP = {
  brain: "fa-brain",
  compass: "fa-compass",
  "heart-pulse": "fa-heart-pulse",
  "notebook-pen": "fa-pen-to-square",
  shield: "fa-shield-halved",
  handshake: "fa-handshake",
  eye: "fa-eye",
  sparkles: "fa-wand-magic-sparkles"
};

export default function Curriculum() {
  return (
    <section id="curriculum" data-testid="curriculum-section" className="relative overflow-hidden bg-ink-800 py-28 md:py-36">
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-25 mix-blend-overlay" />
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" />
              Crash Proof Curriculum
            </div>
            <h2 className="font-display text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              A scalable youth development blueprint —
              <span className="block text-gradient-gold">built for the real world.</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-300">
              Trauma-informed. Evidence-based. Built to be implemented in classrooms,
              programs, and community spaces — not just talked about on a stage.
            </p>

            <div className="mt-10">
              <div className="mb-4 text-xs uppercase tracking-[0.25em] text-zinc-500">Built For</div>
              <div className="flex flex-wrap gap-2">
                {CURRICULUM_AUDIENCES.map((a) => (
                  <span
                    key={a}
                    className="rounded-full border border-gold/20 bg-ink-700/60 px-4 py-2 text-xs uppercase tracking-[0.15em] text-zinc-200"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 border-y border-white/5 py-8">
              {[
                { k: "8 wks", v: "Core Module" },
                { k: "K-12+", v: "Adaptable" },
                { k: "CBT", v: "Trauma-Informed" }
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl text-gold md:text-4xl">{s.k}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#contact"
              data-testid="curriculum-cta-bring"
              className="btn-gold mt-8 inline-flex rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              Bring It To Your School
            </a>
          </motion.div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            {CURRICULUM_FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i }}
                className="group relative overflow-hidden rounded-2xl border border-white/5 bg-ink-700/70 p-6 transition-all hover:-translate-y-0.5 hover:border-gold/40"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gold/5 blur-3xl transition-opacity group-hover:bg-gold/15" />
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-black/40 text-gold">
                    <i className={`fa-solid ${ICON_MAP[f.icon] || "fa-circle"}`} />
                  </span>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Module 0{i + 1}
                  </div>
                </div>
                <div className="mt-5 font-display text-xl text-white">{f.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
